import { useEffect, useState } from "react";

import { api, type AgentInstance } from "../lib/api.ts";
import { formatHostDetail, relTime } from "../lib/time.ts";
import { StatusDot } from "./StatusDot.tsx";

/**
 * Tiny green/amber/gray dot — same data source as <AgentStatusBadge>, but just
 * the indicator (no host lines / labels). Use in compact rows where a full
 * badge would be too tall.
 */
export function AgentStatusDot({ label }: { label: string }) {
	const [state, setState] = useState<"online" | "stuck" | "offline" | null>(null);
	useEffect(() => {
		let cancelled = false;
		async function load() {
			try {
				const res = await api.getAgentStatus(label);
				if (cancelled) return;
				const liveStuck = res.instances.some((i) => i.online && i.stuck);
				const live = res.instances.some((i) => i.online);
				setState(liveStuck ? "stuck" : live ? "online" : "offline");
			} catch {
				if (!cancelled) setState("offline");
			}
		}
		load();
		const id = setInterval(load, 10_000);
		return () => {
			cancelled = true;
			clearInterval(id);
		};
	}, [label]);

	const color = state === "online" ? "emerald" : state === "stuck" ? "amber" : "zinc";
	return <StatusDot color={color} />;
}

/**
 * Polls /api/agents/status every 10s and renders a status dot + summary
 * for the given label. If multiple hosts are running the same agent, they all
 * appear stacked under the badge.
 */
export function AgentStatusBadge({ label }: { label: string }) {
	const [instances, setInstances] = useState<AgentInstance[] | null>(null);

	useEffect(() => {
		let cancelled = false;
		async function load() {
			try {
				const res = await api.getAgentStatus(label);
				if (!cancelled) setInstances(res.instances);
			} catch {
				if (!cancelled) setInstances([]);
			}
		}
		load();
		const id = setInterval(load, 10_000);
		return () => {
			cancelled = true;
			clearInterval(id);
		};
	}, [label]);

	if (instances === null) {
		return <StatusDot color="zinc" text="—" />;
	}

	if (instances.length === 0) {
		return <StatusDot color="zinc" text="Offline" />;
	}

	const online = instances.filter((i) => i.online);
	const offline = instances.filter((i) => !i.online);
	const stuck = online.filter((i) => i.stuck);

	if (online.length === 0) {
		const mostRecent = instances[0]!; // length checked above
		return (
			<div className="flex flex-col gap-1">
				<StatusDot color="zinc" text={`Offline · last seen ${relTime(mostRecent.lastSeen)}`} />
				{offline.map((i) => (
					<HostLine key={i.hostId} instance={i} />
				))}
			</div>
		);
	}

	const headlineColor = stuck.length > 0 ? "amber" : "emerald";
	const headlineText =
		stuck.length > 0
			? online.length === 1
				? "Stuck"
				: `Stuck · ${stuck.length}/${online.length} hosts`
			: online.length === 1
				? "Online"
				: `Online · ${online.length} hosts`;

	return (
		<div className="flex flex-col gap-1">
			<StatusDot color={headlineColor} text={headlineText} />
			{online.map((i) => (
				<HostLine key={i.hostId} instance={i} />
			))}
			{offline.length > 0 && (
				<div className="text-[11px] text-zinc-600 pl-4">
					+ {offline.length} stale {offline.length === 1 ? "instance" : "instances"}
				</div>
			)}
		</div>
	);
}

function HostLine({ instance }: { instance: AgentInstance }) {
	const detail = formatHostDetail(instance);
	return (
		<div className="text-[11px] text-zinc-500 pl-4 font-mono">
			{instance.hostname} · {instance.platform}/{instance.arch} · v{instance.version}
			{detail ? ` · ${detail}` : ""}
		</div>
	);
}
